import styled from '@emotion/styled'
import { Children, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { scrollTo } from '@/core/utils/dom/scrollTo'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useKeyDownArrows } from '@/hooks/useKeyDownArrows'
import { useScrollActivatedItem } from '@/hooks/useScrollActivatedItem'
import { CONFIG } from '@/styles/emotion.config'

import CarouselButtons, { type CarouselButtonsProps } from './CarouselButtons/DefaultButtons'

export interface CarouselProps {
  id?: string
  className?: string
  visibleCountDesktop?: number
  visibleCountMobile?: number
  changeByVisibleCount?: boolean
  itemGap?: 0 | 1 | 2 | 3
  full?: boolean
  children: ReactNode
  renderButtons?: typeof CarouselButtons
  buttonColorModifier?: CarouselButtonsProps['colorModifier']
}

const CarouselStyles = styled.div<Pick<CarouselProps, 'full'>>((props) => ({
  display: 'block',
  position: 'relative',
  transform: 'translate3d(0,0,0)',
  minWidth: '100%',
  whiteSpace: 'nowrap',
  ...(!props.full && { margin: '0 -0.5rem' }),
}))

const CarouselWrapperStyles = styled.div({
  width: '100%',
})

const CarouselItemStyles = styled.li<
  Pick<CarouselProps, 'visibleCountDesktop' | 'visibleCountMobile' | 'itemGap'>
>((props) => ({
  display: 'inline-flex',
  overflow: 'hidden',
  flex: '1 0 auto',
  flexWrap: 'nowrap',
  whiteSpace: 'normal',
  justifyContent: 'center',
  paddingLeft: props.itemGap && `var(--space-${props.itemGap})`,
  paddingRight: props.itemGap && `var(--space-${props.itemGap})`,
  width: '100%',
  maxWidth: 'calc(100% / var(--itemCount))',
  '--itemCount': props.visibleCountDesktop,
  [CONFIG.media.mobileLg]: {
    '--itemCount': props.visibleCountMobile,
  },
}))

const CarouselInnerStyles = styled.ol<{ smoothness: boolean }>(({ smoothness }) => ({
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  overflowX: 'auto',
  scrollBehavior: smoothness ? 'smooth' : 'auto',

  /* Disable scrollbar Firefox */
  '@-moz-document url-prefix()': {
    '&': {
      scrollbarWidth: 'none',
    },
  },

  /* Disable scrollbar Chrome, Webkit */
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}))

const Carousel = ({
  className,
  children,
  visibleCountDesktop = 3,
  visibleCountMobile = 1,
  changeByVisibleCount = false,
  itemGap = 1,
  renderButtons,
  full = false,
  buttonColorModifier = 'primary',
}: CarouselProps) => {
  const carouselRef = useRef<HTMLOListElement>(null)
  const isMobile = useIsMobile()
  const slides = Children.toArray(children)
  const countSlides = slides.length
  const [{ activeMobile, activeDesktop, smoothness }, setCarousel] = useState({
    activeMobile: 0,
    activeDesktop: 0,
    count: countSlides,
    smoothness: false,
  })
  const active = isMobile ? activeMobile : activeDesktop
  const visibleCount = isMobile ? visibleCountMobile : visibleCountDesktop
  const totalSlides = changeByVisibleCount
    ? Math.ceil(slides.length / visibleCount)
    : slides.length - visibleCount + 1

  const currentSlide = changeByVisibleCount ? Math.ceil(active / visibleCount) + 1 : active + 1

  const handleActiveIndex = useCallback(
    (index: number) => {
      if (index === active) {
        return
      }

      setCarousel((state) => {
        if (isMobile) {
          return {
            ...state,
            activeMobile: index,
          }
        }

        return {
          ...state,
          activeDesktop: index,
          smoothness: false,
        }
      })
    },
    [isMobile, active],
  )

  const curryHandleActive = useCallback(
    (action: 'next' | 'prev') => {
      setCarousel((state) => {
        const incrementDecrement = (index: number) => {
          if (changeByVisibleCount) {
            return index + (action === 'next' ? visibleCount : visibleCount * -1)
          }
          return index + (action === 'next' ? 1 : -1)
        }
        const { activeDesktop, activeMobile, count } = state
        const active = incrementDecrement(isMobile ? activeMobile : activeDesktop)
        const lastIsActive = !changeByVisibleCount && active + visibleCount > count
        const isInvalidIndex = active < 0 || active > count
        if (lastIsActive || isInvalidIndex) {
          return state
        }

        if (isMobile) {
          return {
            ...state,
            activeMobile: active,
          }
        }

        return {
          ...state,
          activeDesktop: active,
          smoothness: true,
        }
      })
    },
    [changeByVisibleCount, isMobile, visibleCount],
  )

  const handleNext = useCallback(() => curryHandleActive('next'), [curryHandleActive])
  const handlePrev = useCallback(() => curryHandleActive('prev'), [curryHandleActive])

  const handleOnFocusSlide = useCallback(() => {
    setCarousel((oldState) => ({ ...oldState, smoothness: false }))
  }, [])

  useEffect(() => {
    if (carouselRef.current && active >= 0) {
      let index = active

      if (!isMobile && active + visibleCount === countSlides) {
        index = countSlides - 1
      }

      const element = carouselRef.current?.children.item(index) as HTMLElement
      scrollTo(element)
    }
  }, [active, countSlides, isMobile, visibleCount])

  useEffect(() => {
    if (isMobile)
      setCarousel((oldState) => ({
        ...oldState,
        smoothness: true,
      }))
  }, [isMobile])

  useScrollActivatedItem({
    onActive: handleActiveIndex,
    active,
    ref: carouselRef,
    debounceDelay: visibleCount > 1 ? 400 : 50,
    viewPercentage: visibleCount > 1 ? 10 : 40,
  })

  const { ref: keyDownRef } = useKeyDownArrows({ onRight: handleNext, onLeft: handlePrev })

  useEffect(() => {
    carouselRef.current?.scroll({ left: 0 })
    setCarousel((oldState) => ({
      ...oldState,
      activeMobile: 0,
      activeDesktop: 0,
      count: countSlides,
    }))
  }, [countSlides, isMobile])

  const Buttons = renderButtons || CarouselButtons

  return (
    <CarouselStyles ref={keyDownRef} className={className} full={full}>
      <CarouselWrapperStyles>
        <CarouselInnerStyles ref={carouselRef} role="list" smoothness={smoothness}>
          {slides.map((child, index) => (
            <CarouselItemStyles
              itemGap={itemGap}
              visibleCountDesktop={visibleCountDesktop}
              visibleCountMobile={visibleCountMobile}
              onFocus={handleOnFocusSlide}
              key={`carousel_item_${index}`}
              role="listitem"
            >
              {child}
            </CarouselItemStyles>
          ))}
        </CarouselInnerStyles>
      </CarouselWrapperStyles>
      {totalSlides > 1 && (
        <Buttons
          onNext={handleNext}
          onPrev={handlePrev}
          onActive={handleActiveIndex}
          currentSlide={currentSlide}
          totalSlides={totalSlides}
          colorModifier={buttonColorModifier}
        />
      )}
    </CarouselStyles>
  )
}

export default Carousel
